// /hr/upominky — log finančních upomínek členů týmu.
//
// Pravidlo: max 3 za měsíc, 4. a další = pokuta (variabilní částka zadaná userem).
// Owner+manager mohou přidat upomínku komukoli. Smazat jen owner.
//
// Layout:
//   1) Header s tlačítkem + Přidat upomínku
//   2) KPI: Celkem | Osob na limitu (3+) | Pokut | Suma pokut
//   3) Filtr měsíc + osoba
//   4) Tabulka logu

import React, { useMemo, useState } from "react";
import { AlertTriangle, Plus, Trash2, RefreshCw, Coins, FileText } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import { DlazdiceFiltr, PruhFiltru, ListovaniMesice, EmptyState, SkeletonRows } from "../../components/ui.jsx";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import { apiFetch, useApiData } from "../../lib/api.js";
import { useDemoMode } from "../../lib/DemoModeContext.jsx";
import { getCurrentUser } from "../../lib/auth.js";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import HRUpominkaAddModal from "../../components/HRUpominkaAddModal.jsx";
import { formatDate } from "../../lib/format.js";
import { toast } from "../../components/Toast.jsx";

function fmt(n) { return new Intl.NumberFormat("cs-CZ").format(n || 0); }
function poziceLabel(p) {
  return p === "Trener" ? "Trenér" : (p === "Nutricni specialista" ? "Nutriční specialista" : p || "—");
}

const MONTHS_CS = ["Leden","Únor","Březen","Duben","Květen","Červen","Červenec","Srpen","Září","Říjen","Listopad","Prosinec"];
function mesicLabel(m) {
  if (!m || !/^\d{4}-\d{2}$/.test(m)) return m || "—";
  const [y, mm] = m.split("-");
  return `${MONTHS_CS[parseInt(mm, 10) - 1]} ${y}`;
}

export default function HRUpominky() {
  const user = getCurrentUser();
  const isOwner = user?.role === "owner";
  const { blurred } = useDemoMode();

  const currentMonth = new Date().toISOString().substring(0, 7);
  const [mesic, setMesic] = useState(currentMonth);
  const [addOpen, setAddOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const { data, loading, error, refetch, refreshing } = useApiData(`/api/hr-upominky-list?mesic=${mesic}`);
  const { data: summaryData } = useApiData(`/api/hr-upominky-summary?mesic=${mesic}`);

  const entries = data?.entries || [];
  const summary = summaryData || {};

  const handleDelete = async (id) => {
    try {
      const r = await apiFetch(`/api/hr-upominky-delete?id=${id}`, { method: "DELETE" });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setConfirmDeleteId(null);
      refetch();
    } catch (e) {
      toast("Chyba: " + e.message, "error");
    }
  };

  // Generuj posledních 12 měsíců pro switcher
  const monthOptions = useMemo(() => {
    const arr = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      arr.push({ value: m, label: mesicLabel(m) });
    }
    return arr;
  }, []);

  return (
    <div className={`mx-auto w-full max-w-7xl space-y-4 px-4 py-4 sm:space-y-6 sm:px-5 sm:py-6 ${blurred ? "demo-blur" : ""}`}>
      <GradientHeader
        title="Upomínky"
        icon={AlertTriangle}
        napoveda="Log finančních upomínek. Pravidlo: max 3 upomínky za měsíc na osobu. Čtvrtá a další = pokuta s variabilní částkou. Pořadí v měsíci se přepočítává automaticky po každém přidání nebo smazání. Owner i manager mohou přidat, smazat jen owner."
        akce={[
          { klic: "pridat", ikona: Plus, popis: "Přidat upomínku", onClick: () => setAddOpen(true) },
          { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: refreshing },
        ]}
      />

      {/* Čísla ze vzorníku (rozhodnutí Č2). Upomínky se nefiltrují podle nich,
          jediný filtr je měsíc, ten je pod nimi. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <DlazdiceFiltr popisek="Celkem upomínek" hodnota={summary.totalUpominek || 0} ikona={FileText} ton="znacka" />
        <DlazdiceFiltr popisek="Osob na limitu (3+)" hodnota={summary.atLimit || 0} ikona={AlertTriangle} ton="pozor" />
        <DlazdiceFiltr popisek="Pokut" hodnota={summary.totalPokut || 0} ikona={AlertTriangle} ton="problem" />
        <DlazdiceFiltr popisek="Suma pokut" hodnota={`${fmt(summary.totalCastka || 0)} Kč`} ikona={Coins} ton="problem" />
      </div>

      <PruhFiltru>
        <ListovaniMesice
          popis={mesicLabel(mesic)}
          onZpet={() => setMesic(monthOptions[Math.min(monthOptions.findIndex((o) => o.value === mesic) + 1, monthOptions.length - 1)]?.value || mesic)}
          onVpred={() => setMesic(monthOptions[Math.max(monthOptions.findIndex((o) => o.value === mesic) - 1, 0)]?.value || mesic)}
          zpetVypnuto={monthOptions.findIndex((o) => o.value === mesic) >= monthOptions.length - 1}
          vpredVypnuto={monthOptions.findIndex((o) => o.value === mesic) <= 0}
        />
        <div className="text-xs text-slate-500">
          Zobrazeno <b className="tabular-nums text-slate-800">{entries.length}</b> upomínek
        </div>
      </PruhFiltru>

      {error && <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">Chyba: {error}</div>}

      {loading && <SkeletonRows rows={5} />}

      {!loading && entries.length === 0 && (
        <div className="glass rounded-xl">
          <EmptyState
            icon={AlertTriangle}
            title={`Žádné upomínky v ${mesicLabel(mesic).toLowerCase()}`}
            hint="Přepni měsíc a podívej se na jiné období."
          />
        </div>
      )}

      {!loading && entries.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {entries.map((e) => (
            <UpominkaRow key={e.id} entry={e} canDelete={isOwner} onDelete={() => setConfirmDeleteId(e.id)} />
          ))}
        </div>
      )}

      {addOpen && (
        <HRUpominkaAddModal
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); refetch(); }}
        />
      )}

      <ConfirmDialog
        open={confirmDeleteId != null}
        title="Smazat upomínku?"
        description="Smazání je nevratné. Pořadí ostatních upomínek v měsíci se automaticky přepočítá."
        confirmLabel="Smazat"
        onConfirm={() => handleDelete(confirmDeleteId)}
        onCancel={() => setConfirmDeleteId(null)}
        variant="danger"
      />
    </div>
  );
}


function UpominkaRow({ entry, canDelete, onDelete }) {
  const uzka = useUzkaObrazovka();
  const poradiTon = entry.poradi_v_mesici >= 4 ? "bg-rose-100 text-rose-700"
    : entry.poradi_v_mesici >= 3 ? "bg-amber-100 text-amber-700"
    : "bg-slate-100 text-slate-700";

  if (uzka) {
    return (
      <div className="border-b border-slate-100 px-4 py-3 last:border-b-0">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-bold leading-snug text-slate-900 demo-sensitive">{entry.display_name || entry.person_id}</div>
            <div className="mt-0.5 text-[12px] text-slate-500">{poziceLabel(entry.pozice)}</div>
          </div>
          <span className={`shrink-0 rounded-lg px-2 py-0.5 text-xs font-bold ${poradiTon}`} title={`${entry.poradi_v_mesici}. v měsíci`}>
            {entry.poradi_v_mesici}. v měsíci
          </span>
          {canDelete && (
            <button type="button" onClick={onDelete} title="Smazat" aria-label="Smazat"
              className="btn-ikona shrink-0 border-rose-200 text-rose-500 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="mt-1.5 text-[13px] leading-snug text-slate-700 [overflow-wrap:anywhere]">{entry.duvod}</div>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-slate-500">
          <span>{entry.datum_upominky ? formatDate(entry.datum_upominky) : "—"}</span>
          <span>zadal {entry.zadal || "—"}</span>
          {entry.je_pokuta && (
            <span className="inline-flex items-center gap-1 font-bold text-rose-700">
              <Coins className="h-3.5 w-3.5" /> {entry.castka_pokuty != null ? `${fmt(entry.castka_pokuty)} Kč` : "pokuta"}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-3 items-start px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
      <div className="col-span-3 min-w-0">
        <div className="text-sm font-semibold text-slate-800 truncate demo-sensitive">{entry.display_name || entry.person_id}</div>
        <div className="text-[11px] text-slate-500 mt-0.5">{poziceLabel(entry.pozice)}</div>
      </div>
      <div className="col-span-1 text-xs text-slate-600">
        {entry.datum_upominky ? formatDate(entry.datum_upominky) : "—"}
      </div>
      <div className="col-span-4 text-sm text-slate-700 break-words">{entry.duvod}</div>
      <div className="col-span-1 text-center">
        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-bold ${
          entry.poradi_v_mesici >= 4 ? "bg-rose-100 text-rose-700"
          : entry.poradi_v_mesici >= 3 ? "bg-amber-100 text-amber-700"
          : "bg-slate-100 text-slate-700"
        }`} title={`${entry.poradi_v_mesici}. v měsíci`}>
          {entry.poradi_v_mesici}.
        </span>
      </div>
      <div className="col-span-2 text-right">
        {entry.je_pokuta ? (
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-rose-700">
            <Coins className="w-3.5 h-3.5" /> {entry.castka_pokuty != null ? `${fmt(entry.castka_pokuty)} Kč` : "—"}
          </span>
        ) : (
          <span className="text-xs text-slate-400">bez pokuty</span>
        )}
        <div className="text-[11px] text-slate-500 mt-0.5">{entry.zadal || "—"}</div>
      </div>
      <div className="col-span-1 text-right">
        {canDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="btn-ikona text-rose-600"
            title="Smazat"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
